/**
 * Llamadas HTTP propias del panel.
 *
 * El panel usa el MISMO cliente que el ERP (`lib/api`) para todo lo que pasa por
 * la API generica de modelos: usuarios, empresas y metadata. Lo unico que no
 * puede reusar es `GET /api/v1/admin/roles/`, porque `lib/api` expone funciones
 * por endpoint y no un `request` generico, y ese archivo es compartido: no se
 * toca desde acá.
 *
 * Entonces esta peticion se hace con `fetch` directo, replicando exactamente las
 * dos cosas que importan del cliente: el `Bearer` y la cabecera `X-Company-Id`.
 * Para el refresco de token NO se reimplementa nada: ante un 401 se llama a
 * `api.fetchSession()`, que pasa por el cliente real y dispara su refresco de un
 * solo vuelo; despues se reintenta la peticion con el token ya renovado. Un
 * segundo motor de refresco en paralelo invalidaria el refresh token del primero.
 */

import { ApiError, api } from '@/lib/api';
import type { CompanyRef, ListResponse, ModelSpec, RecordData } from '@/lib/types';
import type { Role } from '@/panel/contract';
import { normalizarRoles } from '@/panel/contract';

const API_BASE = '/api/v1';

/** Ruta del explorador de permisos. Se muestra en pantalla cuando falla. */
export const RUTA_ROLES = `${API_BASE}/admin/roles/`;

function cabeceras(): Record<string, string> {
  const { access, companyId } = api.getAuthState();
  const salida: Record<string, string> = { Accept: 'application/json' };
  if (access !== null) salida.Authorization = `Bearer ${access}`;
  if (companyId !== null) salida['X-Company-Id'] = String(companyId);
  return salida;
}

async function leerCuerpo(respuesta: Response): Promise<unknown> {
  const tipo = respuesta.headers.get('content-type') ?? '';
  if (tipo.includes('application/json')) {
    try {
      return (await respuesta.json()) as unknown;
    } catch {
      return null;
    }
  }
  return null;
}

/** GET autenticado con un unico reintento tras renovar la sesion. */
async function pedir<T>(ruta: string, signal?: AbortSignal): Promise<T> {
  const opciones: RequestInit = { headers: cabeceras(), credentials: 'same-origin' };
  if (signal !== undefined) opciones.signal = signal;

  let respuesta: Response;
  try {
    respuesta = await fetch(`${API_BASE}${ruta}`, opciones);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'No se pudo contactar al servidor.', {}, error);
  }

  if (respuesta.status === 401) {
    // Renovar delegando en el cliente real; si tampoco puede, la sesion murio.
    await api.fetchSession();
    const segunda: RequestInit = { headers: cabeceras(), credentials: 'same-origin' };
    if (signal !== undefined) segunda.signal = signal;
    respuesta = await fetch(`${API_BASE}${ruta}`, segunda);
  }

  if (!respuesta.ok) {
    const cuerpo = await leerCuerpo(respuesta);
    const detalle =
      cuerpo !== null && typeof cuerpo === 'object' && typeof (cuerpo as { detail?: unknown }).detail === 'string'
        ? (cuerpo as { detail: string }).detail
        : `El servidor respondió HTTP ${respuesta.status}.`;
    throw new ApiError(respuesta.status, detalle, {}, cuerpo);
  }

  return (await leerCuerpo(respuesta)) as T;
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
export async function fetchRoles(signal?: AbortSignal): Promise<Role[]> {
  const cuerpo = await pedir<unknown>('/admin/roles/', signal);
  return normalizarRoles(cuerpo);
}

// ---------------------------------------------------------------------------
// Usuarios y empresas (API generica, sin endpoints nuevos)
// ---------------------------------------------------------------------------
/** Campos del usuario que necesita el panel. Se piden explicitos: los m2m no vienen por defecto. */
export const CAMPOS_USUARIO = [
  'name',
  'email',
  'is_active',
  'is_superuser',
  'is_staff',
  'tenant',
  'companies',
  'nucleo_groups',
] as const;

export interface PanelUser extends RecordData {
  name: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_staff: boolean;
  /** Ids de las empresas habilitadas. */
  companies: number[];
  /** Ids de los grupos de seguridad. */
  nucleo_groups: number[];
}

export function fetchUsers(signal?: AbortSignal): Promise<ListResponse<PanelUser>> {
  return api.searchRead<PanelUser>(
    'base.user',
    { fields: [...CAMPOS_USUARIO], order: 'name', limit: 500, include_inactive: true },
    signal,
  );
}

export function fetchUserMeta(signal?: AbortSignal): Promise<ModelSpec> {
  return api.fetchMeta('base.user', null, signal);
}

export function fetchCompanies(signal?: AbortSignal): Promise<ListResponse> {
  return api.searchRead('base.company', { fields: ['name'], order: 'name', limit: 300 }, signal);
}

/** Guarda roles y empresas de un usuario. Un solo PATCH: o entra todo o no entra nada. */
export function saveUserAssignment(
  id: number,
  cambios: { nucleo_groups?: number[]; companies?: number[] },
): Promise<RecordData> {
  return api.updateRecord('base.user', id, cambios);
}

/** Conteo barato de un modelo: pide una fila y se queda con el total. */
export async function contarRegistros(modelo: string, signal?: AbortSignal): Promise<number> {
  const respuesta = await api.searchRead(modelo, { limit: 1, fields: ['id'] }, signal);
  return respuesta.count;
}

export type { CompanyRef };
